import React, { Fragment } from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent, isArrayLike } from '../../../shared/index.js';
import { IDropdownAssets, IDropdownItem } from '../Dropdown/models/index.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import { TranslationContext } from '../Translate/Translate.js';

import { PageNavigation } from './components/PageNavigation.js';
import { PageSizeSelection } from './components/PageSizeSelection.js';
import { PagingResultsStatus } from './components/PagingResultStatus.js';
import { IPagingConfiguration, IPagingProps, IPagingState } from './models/index.js';
import { IPagingValues } from './models/IPagingValues.js';
import Wrapper from './styledComponents/Wrapper.js';
import { PAGING_CATEGORY_KEY, PAGING_COMPONENT_KEY } from './Paging.meta.js';

export class Paging extends ComponentBase<IPagingProps, IPagingState, IPagingConfiguration> {
	categoryKey = PAGING_CATEGORY_KEY;
	componentKey = PAGING_COMPONENT_KEY;

	@observable currentIndex = 0;
	@observable pageSize = 10;

	constructor(props: IPagingProps) {
		super(props);

		this.pageSize = props.pageSize || this.pageSize;
		this.currentIndex = props.currentIndex || this.currentIndex;

		makeObservable(this);
	}

	@action
	fireOnChange = () => {
		const { onChange } = this.props;
		if (onChange) {
			onChange(this.currentIndex, this.pageSize);
		}
	};

	@action
	handlePageBackward = () => {
		const { pageBackward } = this.props;
		this.currentIndex = Math.max(this.currentIndex - this.pageSize, 0);
		if (pageBackward) {
			pageBackward(this.currentIndex);
		}
		this.fireOnChange();
	};

	@action
	handleFirstPage = () => {
		const { pageFirst } = this.props;
		this.currentIndex = 0;
		if (pageFirst) {
			pageFirst(this.currentIndex);
		}
		this.fireOnChange();
	};

	@action
	handleLastPage = () => {
		const { pageLast } = this.props;
		this.currentIndex = (this.pagingValues.totalPages - 1) * this.pageSize;
		if (pageLast) {
			pageLast(this.currentIndex);
		}
		this.fireOnChange();
	};

	@action
	handlePageForward = () => {
		const { pageForward } = this.props;
		this.currentIndex = this.currentIndex + this.pageSize;
		if (pageForward) {
			pageForward(this.currentIndex);
		}
		this.fireOnChange();
	};

	@action
	handlePageSizeChange = (items: IDropdownItem | IDropdownItem[]) => {
		const { pageSizeSelection } = this.props;
		const item = isArrayLike(items) ? items[0] : items;
		const newPageSize = parseInt(item.value, 10);
		this.pageSize = newPageSize;
		this.currentIndex = 0;
		if (pageSizeSelection && pageSizeSelection.callback) {
			pageSizeSelection.callback(this.pageSize, this.currentIndex);
		}
		this.fireOnChange();
	};

	@action
	handlePageChange = (items: IDropdownItem | IDropdownItem[]) => {
		const { pageSelection } = this.props;
		const item = isArrayLike(items) ? items[0] : items;
		const newPage = parseInt(item.value, 10) - 1;
		const newIndex = newPage * this.pageSize;
		this.currentIndex = newIndex;
		if (pageSelection && pageSelection.callback) {
			pageSelection.callback(newPage, this.currentIndex);
		}
		this.fireOnChange();
	};

	componentDidUpdate(previousProps: IPagingProps) {
		const { pageSize, totalRows, currentIndex } = this.props;
		const pageSizeChanged = pageSize !== undefined && previousProps.pageSize !== pageSize;
		const totalRowsChanged = totalRows !== undefined && previousProps.totalRows !== totalRows;
		const currentIndexChanged = currentIndex !== undefined && previousProps.currentIndex !== currentIndex;
		if (pageSizeChanged || totalRowsChanged || currentIndexChanged) {
			this.setCurrentIndex(
				!common.commonUtils.isNullOrUndefined(currentIndex) ? currentIndex : this.currentIndex,
			);
			this.setPageSize(pageSize || this.pageSize);
		}
	}

	@action
	setCurrentIndex = (currentIndex: number) => {
		this.currentIndex = currentIndex;
	};

	@action
	setPageSize = (pageSize: number) => {
		this.pageSize = pageSize;
	};

	@computed
	get pageSizeOptions() {
		const { pageSizeSelection = {} } = this.props;
		let { options } = pageSizeSelection;
		if (!options) {
			options = [this.pageSize];
		}
		return options.map((value) => ({
			value: value.toString(),
			label: value.toString(),
		}));
	}

	@computed
	get dropdownAssets(): IDropdownAssets | undefined {
		return this.props.pageSelection && this.props.pageSelection.dropdownAssets;
	}

	@computed
	get pageOptions(): IDropdownItem[] {
		const options: IDropdownItem[] = [];
		const totalPages = this.pagingValues.totalPages;
		for (let i = 1; i <= totalPages; i++) {
			const pageNumber = i.toString();
			options.push({
				value: pageNumber,
				label: pageNumber,
			});
		}
		return options;
	}

	@computed
	get pagingValues(): IPagingValues {
		const { totalRows, totalEntities = 0 } = this.props;
		const totalPages = Math.ceil((totalRows || 0) / this.pageSize);
		const firstItem = Math.min(totalRows || 0, this.currentIndex + 1);
		const currentPage = Math.ceil((this.currentIndex + 1) / this.pageSize);
		const lastItem = Math.min(totalRows || 0, this.currentIndex + this.pageSize);

		return {
			firstItem,
			lastItem,
			totalRows: totalRows || 0,
			currentPage,
			totalPages,
			totalEntities
		};
	}

	render() {
		const {
			className,
			layout = {},
			ariaControls,
			pageSizeSelection,
			pagingButtons,
			pagingLabelFormat,
			pageSelection,
		} = this.props;
		const { baseOrder = ['PageSizeSelection', 'ResultStatus', 'PageNavigation'] } = layout;
		const { pagingValues } = this;
		return (
			<Wrapper className={className}>
				<TranslationContext.Consumer>
					{(translationContext: ITranslationContextValue) => {
						return (
							<Fragment>
								{baseOrder.map((componentKey) => {
									switch (componentKey) {
										case 'PageSizeSelection':
											return (
												<PageSizeSelection
													key="pageSizeSelection"
													pageSize={this.pageSize}
													pageSizeOptions={this.pageSizeOptions}
													ariaControls={ariaControls}
													pageSizeSelection={pageSizeSelection}
													handlePageSizeChange={this.handlePageSizeChange}
													translationContext={translationContext}
												/>
											);
										case 'ResultStatus':
											return (
												<PagingResultsStatus
													key="pagingResultsStatus"
													pagingLabelFormat={pagingLabelFormat}
													pagingValues={pagingValues}
													translationContext={translationContext}
												/>
											);
										case 'PageNavigation':
											return (
												<PageNavigation
													key="pageNavigation"
													pageOptions={this.pageOptions}
													pagingValues={pagingValues}
													layout={layout}
													pagingButtons={pagingButtons}
													pageSelection={pageSelection}
													handlePageBackward={this.handlePageBackward}
													handlePageForward={this.handlePageForward}
													handlePageChange={this.handlePageChange}
													handleFirstPage={this.handleFirstPage}
													handleLastPage={this.handleLastPage}
													translationContext={translationContext}
												/>
											);
										default:
											return null;
									}
								})}
							</Fragment>
						);
					}}
				</TranslationContext.Consumer>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(observer(Paging), PAGING_CATEGORY_KEY, PAGING_COMPONENT_KEY);
